import React from "react";

import { Box } from "@material-ui/core";
import { MapRef } from "react-map-gl";

import MapBase from "components/visuals/maps/MapBase";
import SelectedStoreMarker from "components/visuals/maps/SelectedStoreMarker";

import Legend from "./Legend";
import Marker from "./Marker";

import { useAppSelector } from "store";
import { selectMapboxConfiguration } from "modules/siteConfiguration/siteConfigurationSlice";
import { selectStore } from "modules/customer/insights/portfolioNew/portfolioSlice";
import {
    selectIsLoading,
    selectHasErrors,
    selectClassifiedCompetitors
} from "modules/customer/insights/portfolioNew/competitionNew/competitionNewSlice";
import { ClassifiedCompetitor } from "modules/customer/insights/portfolioNew/competitionNew/competitors";
import { numberSortExpression, SortDirection } from "utils/sortUtils";
import mathUtils from "utils/mathUtils";

const Map: React.FC = () => {
    const mapRef = React.useRef<MapRef>(null);
    const mapboxConfiguration = useAppSelector(selectMapboxConfiguration);
    const selectedStore = useAppSelector(selectStore);
    const competitors = useAppSelector(selectClassifiedCompetitors);
    const isLoading = useAppSelector(selectIsLoading);
    const hasErrors = useAppSelector(selectHasErrors);

    const [hoveredCompetitor, setHoveredCompetitor] = React.useState<ClassifiedCompetitor | undefined>(undefined);
    const [initialViewport, setInitialViewport] = React.useState({
        latitude: selectedStore?.latitude ?? 0,
        longitude: selectedStore?.longitude ?? 0,
        zoom: 13.5,
        minZoom: 4
    });

    React.useEffect(() => {
        setInitialViewport(current => {
            return {
                ...current,
                latitude: selectedStore?.latitude ?? 0,
                longitude: selectedStore?.longitude ?? 0
            };
        });
    }, [selectedStore]);


    const downloadData = React.useMemo(() => {
        return competitors.map(competitor => {
            return {
                "Store name": competitor.storeName,
                "Brand name": competitor.brandName,
                "Distance from selected store (km)": mathUtils.round(competitor.distanceToStore, 4),
                "Store category": competitor.displayCategoryName,
                "Alternate store categories": competitor.alternateCategoryNames,
                "Store size": competitor.sizeBucket?.label ?? "Unknown",
                "Size range (square feet)": competitor.sizeBucket ? `${competitor.sizeBucket.lowerBound} - ${competitor.sizeBucket.upperBound}` : "",
                "Competitor type": competitor.classification
            };
        }).sort((a, b) => numberSortExpression(a["Distance from selected store (km)"], b["Distance from selected store (km)"], SortDirection.ASC));
    }, [competitors]);
    return (
        <Box data-cy="competition-map">
            <MapBase
                loading={isLoading}
                error={hasErrors}
                title={`Competitors within 10 minutes drive-time of your ${selectedStore?.name} store`}
                mapboxAccessToken={mapboxConfiguration.accessToken}
                mapboxBaseMapStyle={mapboxConfiguration.baseMapStyle}
                initialViewport={initialViewport}
                addGeocoder={false}
                addNavigationControl={true}
                addRecenterButton={true}
                dataCy="competitors-map"
                mapRef={mapRef}
                downloadData={downloadData}
                addFullscreenButton={true}
            >
                <SelectedStoreMarker latitude={selectedStore?.latitude ?? 0} longitude={selectedStore?.longitude ?? 0} />
                {competitors?.map((competitor, index) =>
                    <Marker
                        key={index}
                        competitor={competitor}
                        onTooltipOpen={() => setHoveredCompetitor(competitor)}
                        onTooltipClose={() => setHoveredCompetitor(undefined)}
                    />
                )}
                {/* Need to add nearest client stores here too */}
                {/* {competitors?.map((competitor, index) =>
                    <Marker
                        key={index}
                        competitor={competitor}
                        onTooltipOpen={() => setHoveredCompetitor(competitor)}
                        onTooltipClose={() => setHoveredCompetitor(undefined)}
                    />
                )} */}
                <Legend store={selectedStore} competitor={hoveredCompetitor} />
            </MapBase>
        </Box>
    );
};

export default Map;
